import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaFilter, FaEye, FaUndoAlt, FaTimes } from 'react-icons/fa';
import { Order } from '../../types';
import { generateOrderPDF } from '../../services/pdfService';
import Pagination, { PaginationMeta, EMPTY_PAGINATION, readPaginated } from './Pagination';
import styles from './styles/SalesScreen.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type SalesTab = 'ventas' | 'devoluciones' | 'cambios';

/** Rangos rapidos del filtro de fecha; 'custom' habilita los dos date inputs. */
type DateRange = 'todos' | 'hoy' | '7' | '30' | 'custom';

const DATE_RANGES: { value: DateRange; label: string }[] = [
    { value: 'todos', label: 'Rango de Fecha' },
    { value: 'hoy', label: 'Hoy' },
    { value: '7', label: 'Últimos 7 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: 'custom', label: 'Personalizado' },
];

const PAYMENT_OPTIONS = ['Efectivo', 'Transferencia', 'Debito', 'Credito', 'QR', 'Mixto'];

const money = (n: number) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

const toISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Fecha en el formato corto de la grilla: "Hoy, 12:40", "Ayer" o "24 Oct".
 * El backend manda 'YYYY-MM-DD' en Fecha y la hora aparte en HorarioEntrega.
 */
const formatSaleDate = (order: Order): string => {
    if (!order.Fecha) return '—';
    const parts = order.Fecha.split('-').map(Number);
    if (parts.length < 3 || parts.some(Number.isNaN)) return order.Fecha;

    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today.getTime() - date.getTime()) / 86400000);

    // La hora solo se muestra si es del dia: en el resto aporta poco y alarga la celda.
    if (diffDays === 0) {
        return order.HorarioEntrega ? `Hoy, ${order.HorarioEntrega}` : 'Hoy';
    }
    if (diffDays === 1) return 'Ayer';

    return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

/**
 * Estado del comprobante derivado de los campos que ya guarda el backend.
 * No hay columna de anulacion: anular elimina el pedido, con lo cual una venta
 * anulada desaparece del listado en vez de quedar marcada. Por eso el unico
 * eje disponible hoy es Completada (pagada) vs Pendiente (cobro pendiente).
 */
const saleStatus = (order: Order): string =>
    (order.Pagado === 'Si' ? 'Completada' : 'Pendiente');

const itemCount = (order: Order) =>
    (order.Items || []).reduce((acc, item) => acc + (Number(item.Cantidad) || 0), 0);

interface SalesScreenProps {
    /** Local activo del operador; si se pasa, acota el listado a esa sucursal. */
    idLocal?: string;
}

const SalesScreen: React.FC<SalesScreenProps> = ({ idLocal }) => {
    const [tab, setTab] = useState<SalesTab>('ventas');

    const [orders, setOrders] = useState<Order[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [loading, setLoading] = useState(false);

    // Filtros
    const [range, setRange] = useState<DateRange>('todos');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [local, setLocal] = useState(idLocal || 'Todos');
    const [seller, setSeller] = useState('Todos');
    const [status, setStatus] = useState('Todos');
    const [payment, setPayment] = useState('Todos');

    const [locales, setLocales] = useState<any[]>([]);
    const [detail, setDetail] = useState<Order | null>(null);
    const [toast, setToast] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

    const notify = useCallback((type: 'ok' | 'error', text: string) => {
        setToast({ type, text });
        window.setTimeout(() => setToast(null), 2400);
    }, []);

    useEffect(() => {
        fetch(`${API}/locales`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setLocales(data); })
            .catch(err => console.error('Error cargando locales', err));
    }, []);

    /** Traduce el rango elegido a las fechas que espera /admin/orders. */
    const dateParams = useMemo((): { fecha_inicio?: string; fecha_fin?: string } => {
        const today = new Date();
        if (range === 'hoy') {
            const iso = toISODate(today);
            return { fecha_inicio: iso, fecha_fin: iso };
        }
        if (range === '7' || range === '30') {
            const from = new Date(today);
            from.setDate(from.getDate() - Number(range));
            return { fecha_inicio: toISODate(from), fecha_fin: toISODate(today) };
        }
        if (range === 'custom') {
            return {
                fecha_inicio: customFrom || undefined,
                fecha_fin: customTo || undefined,
            };
        }
        return {};
    }, [range, customFrom, customTo]);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
        if (dateParams.fecha_inicio) params.set('fecha_inicio', dateParams.fecha_inicio);
        if (dateParams.fecha_fin) params.set('fecha_fin', dateParams.fecha_fin);
        if (local !== 'Todos') params.set('id_local', local);
        if (payment !== 'Todos') params.set('metodo_pago', payment);
        if (status !== 'Todos') params.set('estado', status === 'Completada' ? 'Si' : 'No');

        try {
            const res = await fetch(`${API}/admin/orders?${params.toString()}`);
            const data = await res.json();
            const parsed = readPaginated<Order>(data);
            setOrders(parsed.items);
            setMeta(parsed.pagination);
        } catch (err) {
            console.error('Error cargando ventas', err);
            notify('error', 'No se pudieron cargar las ventas');
        } finally {
            setLoading(false);
        }
    }, [page, perPage, dateParams, local, payment, status, notify]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    // Cualquier cambio de filtro vuelve a la primera pagina: quedarse en la 5
    // con un filtro nuevo mostraria una tabla vacia sin motivo aparente.
    useEffect(() => { setPage(1); }, [range, customFrom, customTo, local, payment, status, perPage]);

    /**
     * El vendedor no es un filtro del endpoint, asi que se aplica sobre la
     * pagina ya traida. Se deja explicito para no confundirlo con los demas.
     */
    const visibleOrders = useMemo(
        () => (seller === 'Todos' ? orders : orders.filter(o => (o.Vendedor || '') === seller)),
        [orders, seller],
    );

    /** Vendedores presentes en la pagina actual, para poblar el selector. */
    const sellers = useMemo(() => {
        const names = new Set<string>();
        orders.forEach(o => { if (o.Vendedor) names.add(o.Vendedor); });
        return Array.from(names).sort((a, b) => a.localeCompare(b, 'es'));
    }, [orders]);

    /* -------------------------------------------------------------------- KPIs */

    /**
     * Los KPIs se calculan sobre la pagina cargada, no sobre el total del
     * periodo: el endpoint no expone agregados, y sumar todas las paginas
     * obligaria a traer la tabla entera en cada render.
     */
    const kpis = useMemo(() => {
        const todayISO = toISODate(new Date());
        const delDia = visibleOrders
            .filter(o => o.Fecha === todayISO)
            .reduce((acc, o) => acc + (Number(o.Total) || 0), 0);
        const totalPagina = visibleOrders.reduce((acc, o) => acc + (Number(o.Total) || 0), 0);
        const ticket = visibleOrders.length > 0 ? totalPagina / visibleOrders.length : 0;
        const pendientes = visibleOrders.filter(o => o.Pagado !== 'Si').length;
        return { delDia, totalPagina, ticket, pendientes };
    }, [visibleOrders]);

    /* ---------------------------------------------------------------- acciones */

    /**
     * Anular = eliminar el pedido. El backend ya repone el stock exacto que
     * habia descontado (incluidas promos), asi que no hace falta nada mas aca.
     */
    const voidSale = async (order: Order) => {
        const ok = window.confirm(
            `¿Anular la venta #${order.ID} de ${order.Cliente} por ${money(order.Total)}?\n\n`
            + 'Se repone el stock de los artículos y la venta deja de figurar en el listado. '
            + 'Esta acción no se puede deshacer.'
        );
        if (!ok) return;

        try {
            const res = await fetch(`${API}/admin/orders/${order.ID}`, { method: 'DELETE' });
            if (!res.ok) {
                notify('error', 'No se pudo anular la venta');
                return;
            }
            notify('ok', `Venta #${order.ID} anulada`);
            setDetail(null);
            fetchOrders();
        } catch (err) {
            console.error('Error anulando venta', err);
            notify('error', 'Error de conexión');
        }
    };

    const clearFilters = () => {
        setRange('todos');
        setCustomFrom('');
        setCustomTo('');
        setLocal(idLocal || 'Todos');
        setSeller('Todos');
        setStatus('Todos');
        setPayment('Todos');
    };

    /* ---------------------------------------------------------------- rendered */

    return (
        <div className={styles.sales}>
            <div>
                <h2 className={styles.headTitle}>Ventas y Devoluciones</h2>
                <p className={styles.headSub}>Auditoría de operaciones comerciales y reembolsos</p>
            </div>

            <div className={styles.tabs} role="tablist">
                {([['ventas', 'Ventas'], ['devoluciones', 'Devoluciones'], ['cambios', 'Cambios']] as [SalesTab, string][])
                    .map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                            onClick={() => setTab(key)}
                        >
                            {label}
                        </button>
                    ))}
            </div>

            {tab !== 'ventas' ? (
                <div className={styles.placeholder}>
                    <h3>{tab === 'devoluciones' ? 'Devoluciones' : 'Cambios'}</h3>
                    <p>
                        {tab === 'devoluciones'
                            ? 'Listado de reembolsos con su comprobante original y la nota de crédito asociada.'
                            : 'Listado de cambios de artículos con la diferencia cobrada o acreditada.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className={styles.kpis}>
                        <div className={styles.kpi}>
                            <span className={styles.kpiLabel}>Ventas del día</span>
                            <span className={styles.kpiValue}>{money(kpis.delDia)}</span>
                        </div>
                        <div className={styles.kpi}>
                            <span className={styles.kpiLabel}>Total listado</span>
                            <span className={styles.kpiValue}>{money(kpis.totalPagina)}</span>
                        </div>
                        <div className={styles.kpi}>
                            <span className={styles.kpiLabel}>Ticket promedio</span>
                            <span className={styles.kpiValue}>{money(kpis.ticket)}</span>
                        </div>
                        <div className={styles.kpi}>
                            <span className={styles.kpiLabel}>Pendientes de cobro</span>
                            <span className={styles.kpiValue}>{kpis.pendientes}</span>
                        </div>
                    </div>

                    <div className={styles.filters}>
                        <span className={styles.filtersLabel}><FaFilter /> Filtros:</span>

                        <select value={range} onChange={e => setRange(e.target.value as DateRange)} aria-label="Rango de fecha">
                            {DATE_RANGES.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>

                        {range === 'custom' && (
                            <div className={styles.dateRange}>
                                <input
                                    type="date"
                                    value={customFrom}
                                    onChange={e => setCustomFrom(e.target.value)}
                                    aria-label="Desde"
                                />
                                <span>a</span>
                                <input
                                    type="date"
                                    value={customTo}
                                    onChange={e => setCustomTo(e.target.value)}
                                    aria-label="Hasta"
                                />
                            </div>
                        )}

                        <select value={local} onChange={e => setLocal(e.target.value)} aria-label="Sucursal">
                            <option value="Todos">Sucursal</option>
                            {locales.map(l => (
                                <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                            ))}
                        </select>

                        <select value={seller} onChange={e => setSeller(e.target.value)} aria-label="Vendedor">
                            <option value="Todos">Vendedor</option>
                            {sellers.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>

                        <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Estado">
                            <option value="Todos">Estado</option>
                            <option value="Completada">Completada</option>
                            <option value="Pendiente">Pendiente</option>
                        </select>

                        <select value={payment} onChange={e => setPayment(e.target.value)} aria-label="Método de pago">
                            <option value="Todos">Método Pago</option>
                            {PAYMENT_OPTIONS.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>

                        <button type="button" className={styles.clearBtn} onClick={clearFilters}>
                            Limpiar
                        </button>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>ID de Venta</th>
                                    <th>Fecha</th>
                                    <th>Cliente</th>
                                    <th>Vendedor</th>
                                    <th>Artículos</th>
                                    <th>Total</th>
                                    <th>Método Pago</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr>
                                        <td colSpan={9} className={styles.empty}>Cargando ventas…</td>
                                    </tr>
                                )}

                                {!loading && visibleOrders.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className={styles.empty}>
                                            No hay ventas para los filtros seleccionados.
                                        </td>
                                    </tr>
                                )}

                                {!loading && visibleOrders.map(order => {
                                    const count = itemCount(order);
                                    return (
                                        <tr key={order.ID}>
                                            <td className={styles.cellId}>#VTA-{order.ID}</td>
                                            <td className={styles.cellDate}>{formatSaleDate(order)}</td>
                                            <td>{order.Cliente || 'Consumidor Final'}</td>
                                            <td className={styles.cellSeller}>{order.Vendedor || '—'}</td>
                                            <td className={styles.cellItems}>
                                                {count} {count === 1 ? 'artículo' : 'artículos'}
                                            </td>
                                            <td className={styles.cellTotal}>{money(order.Total)}</td>
                                            <td className={styles.cellPay}>{order.MetodoPago || '—'}</td>
                                            <td>
                                                <span className={styles.badge}>{saleStatus(order)}</span>
                                            </td>
                                            <td>
                                                <div className={styles.actions}>
                                                    <button
                                                        type="button"
                                                        className={styles.iconBtn}
                                                        onClick={() => setDetail(order)}
                                                        aria-label={`Ver detalle de la venta ${order.ID}`}
                                                        title="Ver detalle"
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                                        onClick={() => voidSale(order)}
                                                        aria-label={`Anular la venta ${order.ID}`}
                                                        title="Anular venta"
                                                    >
                                                        <FaUndoAlt />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        meta={meta}
                        onPageChange={setPage}
                        onPerPageChange={setPerPage}
                        itemLabel="ventas"
                    />
                </>
            )}

            {detail && (
                <div
                    className={styles.overlay}
                    role="presentation"
                    onClick={e => { if (e.target === e.currentTarget) setDetail(null); }}
                >
                    <div className={styles.modal} role="dialog" aria-modal="true" aria-label={`Venta ${detail.ID}`}>
                        <div className={styles.modalHead}>
                            <div>
                                <h3>Venta #VTA-{detail.ID}</h3>
                                <p>{formatSaleDate(detail)} · {detail.MetodoPago || 'Sin método'}</p>
                            </div>
                            <button
                                type="button"
                                className={styles.iconBtn}
                                onClick={() => setDetail(null)}
                                aria-label="Cerrar"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailItem}>
                                    <span>Cliente</span>
                                    <strong>{detail.Cliente || 'Consumidor Final'}</strong>
                                </div>
                                <div className={styles.detailItem}>
                                    <span>Vendedor</span>
                                    <strong>{detail.Vendedor || '—'}</strong>
                                </div>
                                <div className={styles.detailItem}>
                                    <span>Estado</span>
                                    <strong>{saleStatus(detail)}</strong>
                                </div>
                                <div className={styles.detailItem}>
                                    <span>Tipo</span>
                                    <strong>{detail.TipoPedido || '—'}</strong>
                                </div>
                            </div>

                            <div className={styles.detailItems}>
                                {(detail.Items || []).map((item, idx) => (
                                    <div key={idx} className={styles.detailLine}>
                                        <span>{item.Cantidad} x {item.Producto}</span>
                                        <strong>{money((item.Precio || 0) * (item.Cantidad || 0))}</strong>
                                    </div>
                                ))}
                            </div>

                            {detail.Observaciones && (
                                <div className={styles.detailItem}>
                                    <span>Observaciones</span>
                                    <strong>{detail.Observaciones}</strong>
                                </div>
                            )}
                        </div>

                        <div className={styles.modalFoot}>
                            <span className={styles.modalTotal}>{money(detail.Total)}</span>
                            <button
                                type="button"
                                className={styles.modalBtn}
                                onClick={() => generateOrderPDF(detail)}
                            >
                                Re-imprimir comprobante
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : ''}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
};

export default SalesScreen;
